import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import {
  CompanyRegistrationData,
  CompanyRegistrationFormState,
  CompanyRegistrationResponse,
  CompanyRegistrationErrorResponse,
} from '../types/company';
import { companyRegistrationSchema } from '../utils/companyValidation';
import { ERROR_MESSAGES } from '../constants/company';

// ステップ定義
export enum RegistrationStep {
  COMPANY_INFO = 0,
  CONTACT_INFO = 1,
  CONFIRMATION = 2,
}

interface StepValidationResult {
  isValid: boolean;
  errors: string[];
}

export const useCompanyRegistration = () => {
  const [formState, setFormState] = useState<CompanyRegistrationFormState>({
    isSubmitting: false,
    submitError: null,
    showSuccessDialog: false,
    registeredCompanyId: null,
  });

  // 現在のステップ状態
  const [currentStep, setCurrentStep] = useState<RegistrationStep>(
    RegistrationStep.COMPANY_INFO
  );

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
    clearErrors,
    trigger,
    reset,
  } = useForm<CompanyRegistrationData>({
    resolver: yupResolver(companyRegistrationSchema) as any,
    mode: 'onChange',
    defaultValues: {
      companyName: '',
      representativeName: '',
      prefecture: '',
      city: '',
      businessType: '' as any,
      employeeCountRange: '' as any,
      contactEmail: '',
      password: '',
      confirmPassword: '',
      agreedToTerms: false,
      agreedToPrivacy: false,
    },
  });

  const watchedValues = watch();

  // フィールドフォーカス時のエラークリア
  const handleFieldFocus = (fieldName: keyof CompanyRegistrationData) => {
    clearErrors(fieldName);
    if (formState.submitError) {
      setFormState((prev) => ({ ...prev, submitError: null }));
    }
  };

  // ステップバリデーション
  const validateStep = async (
    step: RegistrationStep
  ): Promise<StepValidationResult> => {
    const fieldsToValidate: (keyof CompanyRegistrationData)[] = [];

    switch (step) {
      case RegistrationStep.COMPANY_INFO:
        fieldsToValidate.push(
          'companyName',
          'representativeName',
          'prefecture',
          'city',
          'businessType',
          'employeeCountRange'
        );
        break;
      case RegistrationStep.CONTACT_INFO:
        fieldsToValidate.push('contactEmail', 'password', 'confirmPassword');
        break;
      case RegistrationStep.CONFIRMATION:
        fieldsToValidate.push('agreedToTerms', 'agreedToPrivacy');
        break;
    }

    const result = await trigger(fieldsToValidate);
    const stepErrors: string[] = [];

    fieldsToValidate.forEach((field) => {
      if (errors[field]) {
        stepErrors.push(errors[field]?.message || `${field}にエラーがあります`);
      }
    });

    return {
      isValid: result && stepErrors.length === 0,
      errors: stepErrors,
    };
  };

  // 次のステップに進む
  const goToNextStep = async (): Promise<boolean> => {
    const validation = await validateStep(currentStep);

    if (validation.isValid) {
      if (currentStep < RegistrationStep.CONFIRMATION) {
        setCurrentStep((prev) => prev + 1);
        return true;
      }
    } else {
      setFormState((prev) => ({
        ...prev,
        submitError: validation.errors.join(', '),
      }));
    }

    return false;
  };

  // 前のステップに戻る
  const goToPreviousStep = () => {
    if (currentStep > RegistrationStep.COMPANY_INFO) {
      setCurrentStep((prev) => prev - 1);
      // エラーをクリア
      setFormState((prev) => ({ ...prev, submitError: null }));
    }
  };

  // 特定のステップに移動
  const goToStep = (step: RegistrationStep) => {
    setCurrentStep(step);
    setFormState((prev) => ({ ...prev, submitError: null }));
  };

  // 企業登録API呼び出し
  const registerCompany = async (
    data: CompanyRegistrationData
  ): Promise<boolean> => {
    try {
      const { companyService } = await import('../services/companyService');

      // confirmPasswordフィールドを除外してAPIに送信
      const { confirmPassword, ...apiData } = data;
      const response = await companyService.registerCompany(apiData as any);

      setFormState((prev) => ({
        ...prev,
        showSuccessDialog: true,
        registeredCompanyId: response.data.companyId,
      }));

      return true;
    } catch (error: any) {
      console.error('企業登録エラー:', error);

      // カスタムエラーの処理
      if (error.errorCode) {
        const errorConfig =
          ERROR_MESSAGES[error.errorCode as keyof typeof ERROR_MESSAGES] ||
          ERROR_MESSAGES.VALIDATION_ERROR;
        setFormState((prev) => ({
          ...prev,
          submitError: errorConfig.message,
        }));
      } else {
        setFormState((prev) => ({
          ...prev,
          submitError:
            error.message ||
            'システムエラーが発生しました。しばらく時間をおいて再度お試しください。',
        }));
      }

      return false;
    }
  };

  // フォーム送信処理
  const onSubmit = async (data: CompanyRegistrationData) => {
    if (formState.isSubmitting) return;

    setFormState((prev) => ({
      ...prev,
      isSubmitting: true,
      submitError: null,
    }));

    const success = await registerCompany(data);

    setFormState((prev) => ({
      ...prev,
      isSubmitting: false,
    }));

    // 成功時は何もしない（ダイアログが表示される）
    // 失敗時はエラーが設定されている
  };

  // 成功ダイアログを閉じる
  const handleSuccessDialogClose = () => {
    setFormState((prev) => ({
      ...prev,
      showSuccessDialog: false,
    }));
    // ログインページにリダイレクト
    navigate('/auth/login');
  };

  // エラーをクリア
  const clearSubmitError = () => {
    setFormState((prev) => ({
      ...prev,
      submitError: null,
    }));
  };

  // フォームをリセット
  const resetForm = () => {
    reset();
    setCurrentStep(RegistrationStep.COMPANY_INFO);
    setFormState({
      isSubmitting: false,
      submitError: null,
      showSuccessDialog: false,
      registeredCompanyId: null,
    });
  };

  return {
    // React Hook Form
    register,
    handleSubmit: handleSubmit(onSubmit),
    errors,
    isValid,
    setValue,
    watchedValues,
    clearErrors,
    trigger,

    // ステップ管理
    currentStep,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    validateStep,

    // フォーム状態
    isSubmitting: formState.isSubmitting,
    submitError: formState.submitError,
    showSuccessDialog: formState.showSuccessDialog,
    registeredCompanyId: formState.registeredCompanyId,

    // イベントハンドラー
    handleFieldFocus,
    handleSuccessDialogClose,
    clearSubmitError,
    resetForm,
  };
};
